import Decimal from "decimal.js";
import { City } from "utils/city";

export enum AccountStatus {
  ACTIVE = "ACTIVE",
  NEW = "NEW",
  PENDING = "PENDING"
}

export enum Role {
  SUPER_ADMIN = "SUPER_ADMIN",
  ADMIN = "ADMIN",
  SUPPORT = "SUPPORT"
}

export enum UserStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE"
};

export enum DelivererStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE"
}

export enum TransactionStatusType {
  DELIVERED = "DELIVERED",
  PENDING = "PENDING"
};

export enum TransactionDeliveredSubtypeType {
  SUCCESS = "SUCCESS",
  CANCELED = "CANCELED"
};

export enum HistoryType {
  COMFIRMED = "COMFIRMED",
  PENDING = "PENDING",
};

export enum TransactionDirectionType {
  DEBIT = "DEBIT",
  CREDIT = "CREDIT"
};

export enum TransactionType {
  CASH_IN_CASH = 'CASH_IN_CASH',
  CASH_OUT_CASH = 'CASH_OUT_CASH',
  TRANSFER_SKYPAY_TO_BANK = 'TRANSFER_SKYPAY_TO_BANK',
  TRANSFER_SKYPAY_TO_SKYPAY = 'TRANSFER_SKYPAY_TO_SKYPAY',
  TRANSFER_SKYPAY_TO_WALLET = 'TRANSFER_SKYPAY_TO_WALLET',
  TRANSFER_BANK_TO_SKYPAY = 'TRANSFER_BANK_TO_SKYPAY',
  TRANSFER_WALLET_TO_SKYPAY = 'TRANSFER_WALLET_TO_SKYPAY',
  MERCHANT_PAIEMENT_CREDIT = 'MERCHANT_PAIEMENT_CREDIT',
  MERCHANT_PAIEMENT_OFF_US_CREDIT = 'MERCHANT_PAIEMENT_OFF_US_CREDIT',
  FEE_VE_WA_TO_RIB = 'FEE_VE_WA_TO_RIB',
  FEE_TRANSFER = 'FEE_TRANSFER',
  FEE_CASHOUT = 'FEE_CASHOUT',
  FEE_MERCHANT_PAIEMENT = 'FEE_MERCHANT_PAIEMENT',
  FEE_MERCHANT_OFF_US = 'FEE_MERCHANT_OFF_US'
};

export enum CashInAndOutMethod {
  ChaabiCash = "ChaabiCash",
  Tasshilat = "Tasshilat"
}

export type Bank = {
  bankName: string;
  bankLogoName: string;
};

export type AddressType = {
  street: string;
  street2: string;
  city: string;
  country: string;
  zipCode: string;
};

export type UserType = {
  id: string;
  creationDate: Date;
  updateDate: Date;
  email: string;
  fullName: string;
  phoneNumber: string;
  status: boolean;
  officePhoneNumber: string;
  cin: string;
  firstName: string;
  lastName: string;
  address: AddressType,
  role: Role;
};

export type DeliveryMember = {
  uuid: string;
  phoneNumber: string;
  cin: string;
  status: boolean;
  creationDate: Date;
  deactivationDate: Date;
  fullName: string;
  city: City;
  cashOnHand: number;
  firstName: string;
  lastName: string;
}

export type DeliveryMemberCollectionHistoryType = {
  id: string;
  creationDate: Date;
  deliveryPaymentType: CollectionType;
  amount: number;
  reference: string;
  deliveryInvoiceStatus: CollectionStatus;
  paymentDate?: Date;
  cancelationDate?: Date;
  payerFirstName?: string;
  payerLastName?: string;
  payerPhoneNumber?: string;
  transactionId?: string;
}

export type DeliveryMemberReversmentHistoryType = {
  id: string;
  creationDate: Date;
  totalAmount: number;
  transactionReference: string;
  includedInvoicesCount: number;
}

export type DeliveryReversmentIncludedCollectionType = {
  id: string;
  creationDate: Date;
  amount: number;
  reference: string;
  deliveryInvoiceStatus: CollectionStatus;
}

export enum CollectionStatus {
  PAID = "PAID",
  NOT_PAID = "NOT_PAID",
  REVERSED= "REVERSED",
  CANCELED = "CANCELED"
}

export enum CollectionType {
  CASH = "CASH",
  QR_CODE = "QR_CODE",
}

// for the fees, amount is the source (we start with him)
export enum FeeType {
  // totalAmount = ( 1 + (firstValue/100) ) * amount
  // or
  // amount = ( 1 / ( 1 + (firstValue/100) ) ) * totalAmount
  PERCENTAGE = 'PERCENTAGE',
  // totalAmount = amount + firstValue
  // or
  // amount = totalAmount - firstValue
  FIXED = 'FIXED',
  // totalAmount = ( ( 1 + (firstValue/100) ) * amount ) + secondValue
  // or
  // amount = ( totalAmount - secondValue ) * ( 1 / ( 1 + (firstValue/100) ) )
  PERCENTAGE_AND_FIXED = "PERCENTAGE_AND_FIXED",
  // not allowed
  // FIXED_AND_PERCENTAGE = "FIXED_AND_PERCENTAGE"
}

export type FeeObjectType = {
  min: number;
  max: number;
  feesType: FeeType;
  firstValue: number;
  secondValue?: number;
};

export type BankBeneficiaryType = {
  id: number;
  fullName: string;
  destination: string;
};

export type PendingTransactionObjectType = {
  status: number;
  type: TransactionType;
  source: string;
  destination: string;
  sourceName?: string;
  destinationName?: string;
  amountBeforeFees: Decimal;
  fees: Decimal;
  amountAfterFees: Decimal;
  date: Date;
  number?: string;
  cardNumber?: string;
  merchantCategory?: string;
  merchantIconURL?: string;
  method?: CashInAndOutMethod;
  transactionRef?: string;
  pin?: string;
  avatar?: string;
  note?: string
  factureRef?: string;
  facturierTRXNum: string;
  acquereurTRXNum: string;
};

export type ConfirmedTransactionObjectType = {
  transactionRef: string;
  source?: string;
  sourceName?: string;
  destination?: string;
  destinationName?: string;
  amount: Decimal;
  date: Date;
  type: TransactionType;
  method?: string;
  status: TransactionStatusType;
  deliveredSubtype: TransactionDeliveredSubtypeType;
  avatar?: string;
  direction: TransactionDirectionType;
  note?: string;
}

export type WalletBeneficiaryType = {
  id: number;
  fullName: string;
  destination: string;
};

export type SkypayBeneficiaryType = {
  id: number;
  fullName: string;
  destination: string;
  avatarDestination?: string;
};

export enum OtpM2TAction {
  CASHIN = 'CASHIN',
  TRANSFERT = 'TRANSFERT',
  SEND_VIREMENT = 'SEND_VIREMENT',
  CASHOUT = 'CASHOUT',
};

export type RequestsCountType = {
  beneficiaryBankCreationRequestCount: number;
  beneficiarySkypayCreationRequestCount: number;
  beneficiaryWalletCreationRequestCount: number;
  sendMoneyToBankRequestCount: number;
  sendMoneyToSkypayRequestCount: number;
  sendMoneyToWalletRequestCount: number;
};

// add beneficiary demand object returned from backend in demands list
export type ProfessionalAddBeneficiaryRequestType = {
  id: number;
  fullName: string;
  destination: string;
  avatarDestination?: string;
  fullNameRequester: string;
  creationDate: Date;
}

// send money demand object returned from backend in demands list
export type ProfessionalSendMoneyRequestType = {
  id: number;
  fullNameRequester: string;
  fullNameDestination: string;
  phoneNumberDestination: string;
  ribDestination: string;
  note: string;
  creationDate: Date;
  amount: number;
  fees: number;
  avatarDestination?: string;
};

export enum SwitchStatus {
  UNENROLLED = "UNENROLLED",
  ACTIF = "ACTIF",
  INACTIF = "INACTIF",
  UNKNOWN = "UNKNOWN"
}

export enum InternalSwitchStatus {
  ACTIVE = "ACTIVE",
  NOTACTIVE = "NOTACTIVE"
}

export enum SectionStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE"
}

export enum MerchantSection {
  DELIVERY = "DELIVERY",
  ANALYTICS = "ANALYTICS",
}

export type ProfessionalAccountType = {
  walletPhoneNumber: string;
  rib: string;
  balance: string;
  avatar: string;
  companyName: string;
  deliverySectionStatus: SectionStatus;
  analyticsSectionStatus: SectionStatus;
}